import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { api, ApiError } from "../api";
import { useAuth } from "../auth";
import { AuthLayout } from "../components/layouts";
import { Spinner } from "../components/ui";
import { GOOGLE_ERRORS, GoogleLogo } from "./auth-pages";

type LegalDoc = { id: string; title: string; version: string; content: string };

type InviteInfo = {
  course: { title: string; description: string | null; workloadHours: number | null; published: boolean };
  institution: { name: string; slug: string };
  email: string | null;
  problem: string | null;
};

/** Página do link de convite: apresenta o curso e conclui a matrícula do aluno. */
export function InvitePage() {
  const { token = "" } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [google, setGoogle] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [docs, setDocs] = useState<LegalDoc[]>([]);
  const googleError = GOOGLE_ERRORS[new URLSearchParams(location.search).get("erro") ?? ""];

  useEffect(() => {
    api
      .get<{ google?: boolean }>("/auth/config")
      .then((c) => setGoogle(Boolean(c.google)))
      .catch(() => undefined);
    api
      .get<LegalDoc[]>("/legal/current")
      .then(setDocs)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    api
      .get<InviteInfo>(`/invitations/${token}`)
      .then(setInfo)
      .catch((err) => setError(err instanceof ApiError ? err.message : "Convite inválido."));
  }, [token]);

  const back = encodeURIComponent(`/convite/${token}`);
  return (
    <AuthLayout
      title={info ? info.course.title : "Convite"}
      subtitle={info ? `Convite de ${info.institution.name}` : undefined}
    >
      {error ? (
        <p className="alert error">{error}</p>
      ) : !info ? (
        <Spinner />
      ) : (
        <div className="form">
          {info.course.description && <p style={{ color: "var(--ink-2)" }}>{info.course.description}</p>}
          {info.course.workloadHours && (
            <p className="segment-meta">Carga horária: {info.course.workloadHours} h</p>
          )}
          {info.problem ? (
            <p className="alert error">{info.problem} Peça um novo convite ao professor.</p>
          ) : !info.course.published ? (
            <p className="alert warn">O curso ainda será publicado. Guarde este link e volte em breve.</p>
          ) : !user ? (
            <>
              <p className="alert ok">
                Entre na sua conta ou crie uma para começar o curso.
                {info.email ? ` Use o e-mail ${info.email}.` : ""}
              </p>
              {googleError && <p className="alert error">{googleError}</p>}
              {google && (
                <>
                  {docs.length > 0 && (
                    <details>
                      <summary
                        style={{
                          cursor: "pointer",
                          fontSize: "0.9rem",
                          color: "var(--link)",
                          fontWeight: 600,
                        }}
                      >
                        Ler termos de uso e política de privacidade
                      </summary>
                      {docs.map((d) => (
                        <div key={d.id} style={{ marginTop: 10 }}>
                          <div className="label">
                            {d.title} · versão {d.version}
                          </div>
                          <div className="legal-text">{d.content}</div>
                        </div>
                      ))}
                    </details>
                  )}
                  <label className="check" style={{ fontWeight: 400 }}>
                    <input
                      type="checkbox"
                      checked={accepted}
                      onChange={(e) => setAccepted(e.target.checked)}
                    />
                    <span>
                      Li e aceito os termos de uso e a política de privacidade (necessário para criar a conta
                      com o Google).
                    </span>
                  </label>
                  <a
                    className="btn ghost block google-btn"
                    href={`/api/auth/google?convite=${encodeURIComponent(token)}${accepted ? "&aceite=1" : ""}`}
                  >
                    <GoogleLogo />
                    Continuar com o Google
                  </a>
                  <div className="divider-or">ou com e-mail e senha</div>
                </>
              )}
              <Link className="btn block" to={`/entrar?voltar=${back}`}>
                Já tenho conta
              </Link>
              <Link
                className="btn ghost block"
                to={`/cadastro?instituicao=${info.institution.slug}&voltar=${back}`}
              >
                Criar minha conta
              </Link>
            </>
          ) : (
            <>
              <p style={{ color: "var(--ink-2)" }}>
                Você está conectado como <strong>{user.email}</strong>.
              </p>
              <button
                className="btn block"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  setError("");
                  try {
                    await api.post(`/invitations/${token}/accept`);
                    navigate("/aluno?matricula=ok", { replace: true });
                  } catch (err) {
                    setError(
                      err instanceof ApiError ? err.message : "Não foi possível concluir a matrícula.",
                    );
                    setBusy(false);
                  }
                }}
              >
                {busy ? "Matriculando…" : "Entrar no curso"}
              </button>
            </>
          )}
        </div>
      )}
    </AuthLayout>
  );
}

type Enrollments = {
  enrolled: Array<{
    enrollmentId: string;
    courseId: string;
    title: string;
    description: string | null;
    coverUrl: string | null;
    workloadHours: number | null;
    progressPercent: number;
  }>;
  available: Array<{
    courseId: string;
    title: string;
    description: string | null;
    coverUrl: string | null;
    workloadHours: number | null;
  }>;
};

/** Página inicial do aluno: cursos matriculados e cursos com matrícula aberta. */
export function StudentHomePage() {
  const navigate = useNavigate();
  const [data, setData] = useState<Enrollments | null>(null);
  const [msg] = useState(
    new URLSearchParams(location.search).get("matricula") === "ok"
      ? "Matrícula concluída. Bons estudos!"
      : "",
  );
  const load = () =>
    api
      .get<Enrollments>("/enrollments")
      .then(setData)
      .catch(() => setData({ enrolled: [], available: [] }));
  useEffect(() => {
    void load();
  }, []);

  return (
    <main className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">Área do aluno</div>
          <h1>Seus cursos</h1>
        </div>
      </div>
      {msg && <p className="alert ok">{msg}</p>}
      {!data ? (
        <Spinner />
      ) : (
        <>
          {!data.enrolled.length ? (
            <div className="card empty">
              <h2>Nenhum curso por aqui ainda</h2>
              <p>Use o link de convite enviado pelo professor para entrar em um curso.</p>
            </div>
          ) : (
            <div className="course-grid">
              {data.enrolled.map((c) => (
                <article key={c.enrollmentId} className="card course-card">
                  <div className="course-cover">
                    {c.coverUrl ? <img src={c.coverUrl} alt="" /> : <span>{c.title.slice(0, 1)}</span>}
                  </div>
                  <div className="card-pad">
                    <h3>{c.title}</h3>
                    {c.description && <p className="segment-meta clamp">{c.description}</p>}
                    <div className="progress" aria-label={`Progresso ${c.progressPercent}%`}>
                      <span style={{ width: `${Math.max(2, c.progressPercent)}%` }} />
                    </div>
                    <p className="segment-meta" style={{ marginTop: 6 }}>
                      {c.progressPercent}% concluído
                    </p>
                    <Link className="btn small" to={`/aluno/cursos/${c.courseId}`}>
                      {c.progressPercent ? "Continuar" : "Começar"}
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
          <CourseCatalog onEnrolled={(id) => navigate(`/aluno/cursos/${id}`)} />
        </>
      )}
    </main>
  );
}

type CatalogItem = {
  courseId: string;
  title: string;
  description: string | null;
  coverUrl: string | null;
  workloadHours: number | null;
  priceCents: number | null;
  institution: string;
  own: boolean;
};

/** Catálogo: cursos públicos da plataforma e cursos abertos da instituição (treinamentos internos ficam de fora). */
function CourseCatalog({ onEnrolled }: { onEnrolled: (courseId: string) => void }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState<CatalogItem[] | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    const t = setTimeout(
      () =>
        void api
          .get<CatalogItem[]>(`/catalog${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`)
          .then(setItems)
          .catch(() => setItems([])),
      q ? 300 : 0,
    );
    return () => clearTimeout(t);
  }, [q]);
  return (
    <section style={{ marginTop: 32 }}>
      <h2>Explorar cursos</h2>
      <div className="field catalog-search" style={{ maxWidth: 520 }}>
        <label htmlFor="catalog-q">Pesquisar no catálogo</label>
        <input
          id="catalog-q"
          type="search"
          value={q}
          placeholder="Tema, título ou palavra-chave"
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      {error && <p className="alert error">{error}</p>}
      {!items ? (
        <Spinner />
      ) : !items.length ? (
        <p className="segment-meta">
          {q
            ? "Nenhum curso encontrado para esta pesquisa."
            : "Nenhum curso disponível no catálogo por enquanto."}
        </p>
      ) : (
        <div className="course-grid">
          {items.map((c) => (
            <article key={c.courseId} className="card course-card">
              <div className="course-cover">
                {c.coverUrl ? <img src={c.coverUrl} alt="" /> : <span>{c.title.slice(0, 1)}</span>}
              </div>
              <div className="card-pad">
                <h3>{c.title}</h3>
                <p className="segment-meta" style={{ margin: "0 0 6px" }}>
                  {c.own ? "Sua instituição" : c.institution}
                  {c.workloadHours ? ` · ${c.workloadHours} h` : ""}
                  {c.priceCents
                    ? ` · ${(c.priceCents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}`
                    : " · gratuito"}
                </p>
                {c.description && <p className="segment-meta clamp">{c.description}</p>}
                <button
                  className="btn small"
                  onClick={async () => {
                    setError("");
                    try {
                      await api.post(`/enrollments/courses/${c.courseId}`);
                      onEnrolled(c.courseId);
                    } catch (err) {
                      setError(
                        err instanceof ApiError ? err.message : "Não foi possível concluir a matrícula.",
                      );
                    }
                  }}
                >
                  Matricular-me
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
